import { tryParseQuestionnaireIdFromButtonUrl } from "../../messageTemplates/utils/questionnaireTemplateButtonUrl";
import type {
  MessagingQuestionnaireOption,
  MessagingTemplate,
} from "../types";

/**
 * Template → "exige questionário?"
 *
 * Regra atual:
 * - Se BODY/HEADER contiverem a variável `questionnaire_link`, o usuário deve selecionar
 *   um questionário antes do envio.
 *
 * Observação:
 * - Mantemos a regra simples e explícita para evitar inferências escondidas na UI.
 * - Esta função é pura e fácil de evoluir (ex.: múltiplas variáveis).
 * - Questionário já vinculado no botão (`buttonValue`) NÃO exige seleção: o id vem da URL.
 */

export const QUESTIONNAIRE_LINK_VARIABLE_NAME = "questionnaire_link";

function textIncludesQuestionnaireVariable(text: string): boolean {
  const normalized = String(text ?? "").toLowerCase();
  // Variáveis do WhatsApp no projeto seguem o padrão `{{var}}`.
  return normalized.includes(`{{${QUESTIONNAIRE_LINK_VARIABLE_NAME}}}`);
}

export function doesTemplateRequireQuestionnaire(
  template: MessagingTemplate | null | undefined,
): boolean {
  if (!template) return false;

  const hasInBody = textIncludesQuestionnaireVariable(template.preview);
  if (hasInBody) return true;

  const headerText = String(template.header ?? "").trim();
  return headerText ? textIncludesQuestionnaireVariable(headerText) : false;
}

/**
 * Extrai o Questionnaire id já gravado na URL do botão do template (criação do modelo).
 * Reutiliza o parser existente — não duplica a leitura de query/hash.
 */
export function resolveLinkedQuestionnaireIdFromTemplate(
  template: MessagingTemplate | null | undefined,
): string | null {
  return tryParseQuestionnaireIdFromButtonUrl(template?.buttonValue);
}

/**
 * Questionário efetivo para criar Task após o envio WhatsApp.
 *
 * Ordem:
 * 1. Escolha do Autocomplete (fluxo `{{questionnaire_link}}`).
 * 2. Id parseado de `buttonValue` + título na lista (fallback: "Questionário vinculado").
 */
export function resolveQuestionnaireForSend(params: {
  template: MessagingTemplate | null | undefined;
  selectedQuestionnaire: MessagingQuestionnaireOption | null | undefined;
  questionnaires: MessagingQuestionnaireOption[];
}): MessagingQuestionnaireOption | null {
  const selectedId = String(params.selectedQuestionnaire?.id ?? "").trim();
  if (selectedId && params.selectedQuestionnaire) {
    return params.selectedQuestionnaire;
  }

  const linkedId = resolveLinkedQuestionnaireIdFromTemplate(params.template);
  if (!linkedId) return null;

  const fromList = params.questionnaires.find(
    (questionnaire) => questionnaire.id === linkedId,
  );
  if (fromList) return fromList;

  return {
    id: linkedId,
    title: "Questionário vinculado",
  };
}

